
import { supabase } from '@/integrations/supabase/client';

export interface UserPoints {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  total_points: number;
  current_level: string;
  rank_position: number | null;
  referral_code: string;
  referred_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface PointsTransaction {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  points: number;
  transaction_type: 'referral' | 'profile_complete' | 'join_team' | 'participate_event' | 'daily_login' | 'task_complete' | 'welcome_bonus';
  description: string | null;
  metadata: any;
  created_at: string;
}

export interface UserTask {
  id: string;
  user_id: string;
  user_type: 'supabase' | 'internal';
  task_type: string;
  task_description: string;
  points_reward: number;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
}

export const pointsService = {
  // Get user points
  async getUserPoints(userId: string, userType: 'supabase' | 'internal'): Promise<UserPoints | null> {
    try {
      const { data, error } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data as UserPoints;
    } catch (error) {
      console.error('Error fetching user points:', error);
      return null;
    }
  },

  // Initialize user points when they first sign up
  async initializeUserPoints(userId: string, userType: 'supabase' | 'internal'): Promise<UserPoints | null> {
    try {
      const { data, error } = await supabase.rpc('award_points', {
        p_user_id: userId,
        p_user_type: userType,
        p_points: 0,
        p_transaction_type: 'daily_login',
        p_description: 'Welcome to the platform!'
      });

      if (error) throw error;

      return await this.getUserPoints(userId, userType);
    } catch (error) {
      console.error('Error initializing user points:', error);
      return null;
    }
  },

  // Award points to user
  async awardPoints(
    userId: string, 
    userType: 'supabase' | 'internal', 
    points: number, 
    transactionType: PointsTransaction['transaction_type'],
    description?: string,
    metadata?: any
  ): Promise<string | null> {
    try {
      const { data, error } = await supabase.rpc('award_points', {
        p_user_id: userId,
        p_user_type: userType,
        p_points: points,
        p_transaction_type: transactionType,
        p_description: description,
        p_metadata: metadata
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error awarding points:', error);
      return null;
    }
  },

  // Process referral
  async processReferral(referralCode: string, newUserId: string, newUserType: 'supabase' | 'internal'): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('process_referral', {
        p_referral_code: referralCode,
        p_new_user_id: newUserId,
        p_new_user_type: newUserType
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error processing referral:', error);
      return false;
    }
  },

  // Get user transactions
  async getUserTransactions(userId: string, userType: 'supabase' | 'internal'): Promise<PointsTransaction[]> {
    try {
      const { data, error } = await supabase
        .from('points_transactions')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as PointsTransaction[];
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  },

  // Get user tasks
  async getUserTasks(userId: string, userType: 'supabase' | 'internal'): Promise<UserTask[]> {
    try {
      const { data, error } = await supabase
        .from('user_tasks')
        .select('*')
        .eq('user_id', userId)
        .eq('user_type', userType)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as UserTask[];
    } catch (error) {
      console.error('Error fetching tasks:', error);
      return [];
    }
  },

  // Complete a task
  async completeTask(taskId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_tasks')
        .update({ 
          completed: true, 
          completed_at: new Date().toISOString() 
        })
        .eq('id', taskId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error completing task:', error);
      return false;
    }
  },

  // Create initial tasks for new users
  async createInitialTasks(userId: string, userType: 'supabase' | 'internal'): Promise<void> {
    const tasks = [
      {
        user_id: userId,
        user_type: userType,
        task_type: 'profile_complete',
        task_description: 'Complete Profile',
        points_reward: 100
      },
      {
        user_id: userId,
        user_type: userType,
        task_type: 'join_team',
        task_description: 'Join a Team',
        points_reward: 50
      },
      {
        user_id: userId,
        user_type: userType,
        task_type: 'participate_event',
        task_description: 'Participate in Events',
        points_reward: 25
      },
      {
        user_id: userId,
        user_type: userType,
        task_type: 'daily_login',
        task_description: 'Daily Login',
        points_reward: 5
      }
    ];

    try {
      const { error } = await supabase
        .from('user_tasks')
        .insert(tasks);

      if (error) throw error;
    } catch (error) {
      console.error('Error creating initial tasks:', error);
    }
  },

  // Get leaderboard
  async getLeaderboard(limit: number = 10): Promise<UserPoints[]> {
    try {
      const { data, error } = await supabase
        .from('user_points')
        .select('*')
        .order('total_points', { ascending: false })
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;
      return (data || []) as UserPoints[];
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      return [];
    }
  }
};
